import { useEffect, useRef, useCallback } from 'react';
import { apiRequest } from '@/lib/queryClient';

interface ScrollJumpEvent {
  timestamp: number;
  oldPosition: number;
  newPosition: number;
  jumpDistance: number;
  wasUserInitiated: boolean;
  trigger: 'programmatic' | 'focus' | 'unknown';
  url: string;
}

/**
 * Hook to monitor scroll position and detect unexpected jumps
 * Only logs when audit logging is enabled on the server
 */
export function useScrollMonitoring() {
  const lastScrollPositionRef = useRef(0);
  const isUserScrollingRef = useRef(false);
  const userScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTimeRef = useRef(Date.now());
  const isLoggingEnabledRef = useRef<boolean | null>(null);

  // Check if logging is enabled
  const checkLoggingEnabled = useCallback(async () => {
    if (isLoggingEnabledRef.current === null) {
      try {
        const response = await apiRequest('GET', '/api/system-settings');
        if (response.ok) {
          const settings = await response.json();
          isLoggingEnabledRef.current = settings.performanceLoggingEnabled || false;
        } else {
          isLoggingEnabledRef.current = false;
        }
      } catch {
        isLoggingEnabledRef.current = false;
      }
    }
    return isLoggingEnabledRef.current;
  }, []);

  // Log scroll jump to audit system
  const logScrollJump = useCallback(async (event: ScrollJumpEvent) => {
    const loggingEnabled = await checkLoggingEnabled();
    if (!loggingEnabled) return;

    try {
      await apiRequest('POST', '/api/audit-logs/scroll-jump', event);
    } catch (error) {
      // Silently fail to avoid disrupting the user experience
      console.debug('Failed to log scroll jump:', error);
    }
  }, [checkLoggingEnabled]);

  // Track user-initiated scrolling
  const handleUserScroll = useCallback(() => {
    isUserScrollingRef.current = true;
    
    // Clear existing timeout
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }
    
    // Reset user scrolling flag after a delay
    userScrollTimeoutRef.current = setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 150); // 150ms window for user scrolling
  }, []);

  // Monitor scroll position changes
  const handleScroll = useCallback(() => {
    const currentPosition = window.scrollY;
    const currentTime = Date.now();
    const timeDiff = currentTime - lastScrollTimeRef.current;
    
    // Only check for jumps if enough time has passed to avoid noise
    if (timeDiff > 50) {
      const oldPosition = lastScrollPositionRef.current;
      const jumpDistance = Math.abs(currentPosition - oldPosition);
      
      // Detect significant scroll jumps (more than 100px in less than 100ms)
      // and not user-initiated
      if (jumpDistance > 100 && timeDiff < 100 && !isUserScrollingRef.current) {
        const scrollJumpEvent: ScrollJumpEvent = {
          timestamp: currentTime,
          oldPosition,
          newPosition: currentPosition,
          jumpDistance,
          wasUserInitiated: false,
          trigger: 'programmatic', // Could be enhanced to detect focus vs other causes
          url: window.location.href
        };
        
        logScrollJump(scrollJumpEvent);
      }
    }
    
    lastScrollPositionRef.current = currentPosition;
    lastScrollTimeRef.current = currentTime;
  }, [logScrollJump]);

  // Track mouse and touch events to detect user interaction
  const handleUserInteraction = useCallback(() => {
    handleUserScroll();
  }, [handleUserScroll]);

  useEffect(() => {
    // Initialize position
    lastScrollPositionRef.current = window.scrollY;
    lastScrollTimeRef.current = Date.now();

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Add user interaction listeners
    window.addEventListener('mousedown', handleUserInteraction, { passive: true });
    window.addEventListener('touchstart', handleUserInteraction, { passive: true });
    window.addEventListener('wheel', handleUserInteraction, { passive: true });
    window.addEventListener('keydown', handleUserInteraction, { passive: true });

    return () => {
      // Cleanup
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousedown', handleUserInteraction);
      window.removeEventListener('touchstart', handleUserInteraction);
      window.removeEventListener('wheel', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, [handleScroll, handleUserInteraction]);

  // Reset logging enabled cache when settings might change
  const resetLoggingCache = useCallback(() => {
    isLoggingEnabledRef.current = null;
  }, []);

  return {
    resetLoggingCache
  };
}